import { useState, useEffect } from 'react';
import { checkProductNameUniqueness } from '../fetch/checkUniqueness';
import { UNIQUENESS_STATUS } from '../../constants/uniquenessStatus';

/**
 * Custom hook per verificare l'unicità del nome prodotto con debounce
 * @param {string} productName - Nome del prodotto da verificare
 * @param {string} token - Token di autenticazione
 * @param {string} productId - ID del prodotto (opzionale, per la modifica)
 * @param {string} originalName - Nome originale del prodotto (opzionale, per la modifica)
 * @returns {Object} { isUnique, uniqueStatus }
 */
export const useProductNameUniqueness = (productName, token, productId = null, originalName = '') => {
    const [isUnique, setIsUnique] = useState(true);
    const [uniqueStatus, setUniqueStatus] = useState(UNIQUENESS_STATUS.IDLE);

    useEffect(() => {
        // Se è una modifica e il nome è uguale all'originale, è sempre univoco
        if (productId && productName === originalName) {
            setUniqueStatus(UNIQUENESS_STATUS.IDLE);
            setIsUnique(true);
            return;
        }

        // Se il campo è vuoto, non è univoco
        if (!productName || !productName.trim()) {
            setUniqueStatus(UNIQUENESS_STATUS.IDLE);
            setIsUnique(false);
            return;
        }

        const timeoutId = setTimeout(async () => {
            setUniqueStatus(UNIQUENESS_STATUS.CHECKING);

            const { error, data } = await checkProductNameUniqueness({
                token,
                name: productName.trim(),
                productId,
            });

            if (error) {
                setUniqueStatus(UNIQUENESS_STATUS.IDLE);
                setIsUnique(false);
                return;
            }

            const unique = data?.unique === true;
            setUniqueStatus(unique ? UNIQUENESS_STATUS.UNIQUE : UNIQUENESS_STATUS.NOT_UNIQUE);
            setIsUnique(unique);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [productName, token, productId, originalName]);

    return { isUnique, uniqueStatus };
};

export default useProductNameUniqueness;
